import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { PageLoader } from '../components/PageLoader';
import { KeyedRouteErrorBoundary } from '../components/RouteErrorBoundary';
import { reloadForStaleDeploy } from './deployRecovery';

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('error loading dynamically imported module') ||
    (error.name === 'TypeError' && msg.includes('import'))
  );
}

async function retryImport<T>(factory: () => Promise<T>, retries = 2): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error) || attempt === retries) break;
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }

  if (isChunkLoadError(lastError)) {
    reloadForStaleDeploy();
    return new Promise(() => {});
  }

  throw lastError;
}

function wrapLazyRoute(Inner: LazyExoticComponent<ComponentType<unknown>>) {
  return function LazyRoutePage() {
    return (
      <Suspense fallback={<PageLoader />}>
        <KeyedRouteErrorBoundary>
          <Inner />
        </KeyedRouteErrorBoundary>
      </Suspense>
    );
  };
}

export function lazyNamed<T extends Record<string, unknown>>(
  factory: () => Promise<T>,
  name: keyof T
) {
  const Inner = lazy(() =>
    retryImport(factory).then((module) => ({
      default: module[name] as ComponentType<unknown>,
    }))
  );
  return wrapLazyRoute(Inner);
}

export function lazyDefault(factory: () => Promise<{ default: ComponentType<unknown> }>) {
  const Inner = lazy(() => retryImport(factory));
  return wrapLazyRoute(Inner);
}
